import React from 'react';
import { Book, Scale, Sparkles, Gauge } from 'lucide-react';
import { MenuOptions } from '../../../types/Menu';
import { motion } from 'framer-motion';

interface AdditionalOptionsProps {
  options: MenuOptions;
  onToggleOption: (option: keyof MenuOptions) => void;
}

export const AdditionalOptions: React.FC<AdditionalOptionsProps> = ({
  options,
  onToggleOption,
}) => {
  const additionalFeatures = [
    { 
      key: 'showPreparation',
      label: 'Pasos de preparación',
      description: 'Instrucciones detalladas paso a paso',
      icon: Book,
      color: 'bg-blue-500'
    },
    { 
      key: 'showNutritionalInfo',
      label: 'Información nutricional',
      description: 'Calorías y macronutrientes por ración',
      icon: Scale,
      color: 'bg-green-500'
    },
    { 
      key: 'showAlternatives',
      label: 'Alternativas',
      description: 'Sugerencias para personalizar la receta',
      icon: Sparkles,
      color: 'bg-purple-500'
    },
    { 
      key: 'useThermomix',
      label: 'Modo Thermomix',
      description: 'Adaptar la receta para Thermomix',
      icon: Gauge,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h4 className="font-medium mb-6">Características adicionales</h4>
      <div className="grid gap-4">
        {additionalFeatures.map(({ key, label, description, icon: Icon, color }) => (
          <motion.div
            key={key}
            whileHover={{ scale: 1.02 }}
            className={`relative overflow-hidden rounded-lg transition-all ${
              options[key as keyof MenuOptions]
                ? 'bg-gradient-to-r from-gray-50 to-orange-50 shadow-md'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <label className="flex items-start gap-4 p-4 cursor-pointer">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={options[key as keyof MenuOptions] as boolean}
                  onChange={() => onToggleOption(key as keyof MenuOptions)}
                  className="peer sr-only"
                />
                <div className={`w-12 h-12 rounded-lg ${
                  options[key as keyof MenuOptions] ? color : 'bg-gray-200'
                } transition-colors flex items-center justify-center`}>
                  <Icon 
                    size={24} 
                    className={options[key as keyof MenuOptions] ? 'text-white' : 'text-gray-500'} 
                  />
                </div>
              </div>
              <div className="flex-1">
                <span className="block font-medium text-gray-900 mb-1">{label}</span>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 transition-colors ${
                options[key as keyof MenuOptions]
                  ? `${color} border-transparent`
                  : 'border-gray-300 bg-white'
              }`}>
                {options[key as keyof MenuOptions] && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-full h-full text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </motion.svg>
                )}
              </div>
            </label>
          </motion.div>
        ))}
      </div>
    </div>
  );
};